import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getDashboard = createAsyncThunk(
    'dashboard/getDashboard',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) throw new Error('No token available');

            console.log('Fetching dashboard with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD, { params });
            console.log('Dashboard response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Dashboard retrieved successfully',
                    data: data || {}
                };
            }

            return rejectWithValue({
                message: message || 'Failed to fetch dashboard',
                status,
                apiResponse: response.data
            });
        } catch (error) {
            console.error('Get dashboard error:', error);

            let errorMessage = 'Failed to fetch dashboard';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch dashboard';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ---------- Individual widgets (for on-demand refresh) ----------
export const getDashboardStats = createAsyncThunk(
    'dashboard/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return { success: true, message: message || 'Stats retrieved', stats: data || {} };
            }
            return rejectWithValue({ message: message || 'Failed to fetch stats', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch stats',
                status: error.response?.status || 500
            });
        }
    }
);

export const getSmsActivity = createAsyncThunk(
    'dashboard/getSmsActivity',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_SMS_ACTIVITY, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return { success: true, message: message || 'SMS activity retrieved', smsActivity: data || {} };
            }
            return rejectWithValue({ message: message || 'Failed to fetch SMS activity', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch SMS activity',
                status: error.response?.status || 500
            });
        }
    }
);

export const getBirthdays = createAsyncThunk(
    'dashboard/getBirthdays',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_BIRTHDAYS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthdays retrieved',
                    birthdays: data?.members || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch birthdays', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch birthdays',
                status: error.response?.status || 500
            });
        }
    }
);

export const getInboxAlerts = createAsyncThunk(
    'dashboard/getInboxAlerts',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_INBOX_ALERTS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return { success: true, message: message || 'Inbox alerts retrieved', inboxAlerts: data || [] };
            }
            return rejectWithValue({ message: message || 'Failed to fetch inbox alerts', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch inbox alerts',
                status: error.response?.status || 500
            });
        }
    }
);

export const getAutomations = createAsyncThunk(
    'dashboard/getAutomations',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_AUTOMATIONS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Automations retrieved',
                    automations: data?.automations || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch automations', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch automations',
                status: error.response?.status || 500
            });
        }
    }
);

export const getPlatformHealth = createAsyncThunk(
    'dashboard/getPlatformHealth',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_PLATFORM_HEALTH, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return { success: true, message: message || 'Health retrieved', platformHealth: data || {} };
            }
            return rejectWithValue({ message: message || 'Failed to fetch platform health', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch platform health',
                status: error.response?.status || 500
            });
        }
    }
);

export const getLatestPoll = createAsyncThunk(
    'dashboard/getLatestPoll',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_LATEST_POLL, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return { success: true, message: message || 'Latest poll retrieved', latestPoll: data || null };
            }
            return rejectWithValue({ message: message || 'Failed to fetch latest poll', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch latest poll',
                status: error.response?.status || 500
            });
        }
    }
);

export const getRecentActivity = createAsyncThunk(
    'dashboard/getRecentActivity',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_RECENT_ACTIVITY, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Recent activity retrieved',
                    recentActivity: data?.items || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch recent activity', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch recent activity',
                status: error.response?.status || 500
            });
        }
    }
);

export const getGroupsSnapshot = createAsyncThunk(
    'dashboard/getGroupsSnapshot',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.DASHBOARD_GROUPS_SNAPSHOT, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Groups snapshot retrieved',
                    groupsSnapshot: data?.groups || []
                };
            }
            return rejectWithValue({ message: message || 'Failed to fetch groups snapshot', status });
        } catch (error) {
            return rejectWithValue({
                message: error.response?.data?.message || error.message || 'Failed to fetch groups snapshot',
                status: error.response?.status || 500
            });
        }
    }
);

/* =============================================================
 *  INITIAL STATE
 * ============================================================= */
const initialState = {
    // Aggregate
    stats: null,
    smsActivity: { bars: [], totals: {} },
    birthdays: [],
    inboxAlerts: [],
    automations: [],
    platformHealth: { items: [], status: null, message: null, checked_at: null },
    latestPoll: null,
    recentActivity: [],
    groupsSnapshot: [],
    generatedAt: null,

    // Meta
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

/* =============================================================
 *  SLICE
 * ============================================================= */
const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        clearError: (state) => { state.error = null; },
        clearSuccessMessage: (state) => { state.successMessage = null; },
        clearApiMessage: (state) => { state.apiMessage = null; },
        clearDashboardState: (state) => {
            Object.assign(state, initialState);
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET DASHBOARD (aggregate) =====
            .addCase(getDashboard.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getDashboard.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const data = payload.data || {};

                state.stats          = data.stats || null;
                state.smsActivity    = data.sms_activity || { bars: [], totals: {} };
                state.birthdays      = data.birthdays || [];
                state.inboxAlerts    = data.inbox_alerts || [];
                state.automations    = data.automations || [];
                state.platformHealth = data.platform_health || { items: [] };
                state.latestPoll     = data.latest_poll || null;
                state.recentActivity = data.recent_activity || [];
                state.groupsSnapshot = data.groups_snapshot || [];
                state.generatedAt    = data.generated_at || null;

                state.successMessage = payload.message || 'Dashboard retrieved successfully';
                state.apiMessage     = payload.message || 'Dashboard retrieved successfully';
                state.error = null;
            })
            .addCase(getDashboard.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch dashboard';
                state.apiMessage = action.payload?.message || 'Failed to fetch dashboard';
                state.successMessage = null;
            })

            // ===== Individual widget thunks =====
            .addCase(getDashboardStats.fulfilled, (state, action) => {
                state.stats = action.payload?.stats || null;
            })
            .addCase(getSmsActivity.fulfilled, (state, action) => {
                state.smsActivity = action.payload?.smsActivity || { bars: [], totals: {} };
            })
            .addCase(getBirthdays.fulfilled, (state, action) => {
                state.birthdays = action.payload?.birthdays || [];
            })
            .addCase(getInboxAlerts.fulfilled, (state, action) => {
                state.inboxAlerts = action.payload?.inboxAlerts || [];
            })
            .addCase(getAutomations.fulfilled, (state, action) => {
                state.automations = action.payload?.automations || [];
            })
            .addCase(getPlatformHealth.fulfilled, (state, action) => {
                state.platformHealth = action.payload?.platformHealth || { items: [] };
            })
            .addCase(getLatestPoll.fulfilled, (state, action) => {
                state.latestPoll = action.payload?.latestPoll || null;
            })
            .addCase(getRecentActivity.fulfilled, (state, action) => {
                state.recentActivity = action.payload?.recentActivity || [];
            })
            .addCase(getGroupsSnapshot.fulfilled, (state, action) => {
                state.groupsSnapshot = action.payload?.groupsSnapshot || [];
            });
    },
});

/* =============================================================
 *  ACTIONS
 * ============================================================= */
export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearDashboardState,
} = dashboardSlice.actions;

/* =============================================================
 *  SELECTORS
 * ============================================================= */
export const selectDashboardStats          = (state) => state.dashboard?.stats || null;
export const selectSmsActivity             = (state) => state.dashboard?.smsActivity || { bars: [], totals: {} };
export const selectBirthdays               = (state) => state.dashboard?.birthdays || [];
export const selectInboxAlerts             = (state) => state.dashboard?.inboxAlerts || [];
export const selectAutomations             = (state) => state.dashboard?.automations || [];
export const selectPlatformHealth          = (state) => state.dashboard?.platformHealth || { items: [] };
export const selectLatestPoll              = (state) => state.dashboard?.latestPoll || null;
export const selectRecentActivity          = (state) => state.dashboard?.recentActivity || [];
export const selectGroupsSnapshot          = (state) => state.dashboard?.groupsSnapshot || [];
export const selectDashboardGeneratedAt    = (state) => state.dashboard?.generatedAt || null;

export const selectDashboardLoading        = (state) => state.dashboard?.isLoading || false;
export const selectDashboardError          = (state) => state.dashboard?.error || null;
export const selectDashboardSuccess        = (state) => state.dashboard?.successMessage || null;
export const selectDashboardApiMessage     = (state) => state.dashboard?.apiMessage || null;

export default dashboardSlice.reducer;